import React, { useRef, useEffect, useState, useContext } from "react";
import CustomDivider from "./CustomDivider";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  CircularProgress,
} from "@mui/material";
import "./Table.css";
import { toPng } from "html-to-image";
import SurveyManagementService from "services/SurveyManagementService";
import SignaturePage from "./SignaturePage";
import ViewImage from "./ViewImage";
import { useTranslation } from "react-i18next";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { SnackbarContext } from "context/snackBarContext";

const ViewSurvey = ({
  surveyData, // This is the selected row data from survey table
  isVisitCompliance,
  selectedCountry,
  onBack,
  onShowSignature,
  onRefreshData,
  isApprover,
  isGlobalProfile,
  selectedCropStage,
  selectedCropStageId,
  year,
}) => {
  const signatureRef = useRef(null);
  const [viewSurveyData, setViewSurveyData] = useState([]); // Detailed survey data from API
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [downloadPdfModalOpen, setDownloadPdfModalOpen] = useState(false);
  const [signatureAlertOpen, setSignatureAlertOpen] = useState(false);
  const [reopenComments, setReopenComments] = useState("");
  const [moduleNames, setModuleNames] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [signaturePageLoaded, setSignaturePageLoaded] = useState(false);
  const { t } = useTranslation();
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
  const status = surveyData?.status?.toLowerCase();
  const headerId = surveyData?.headerId;
  const cropStageTitle = selectedCropStage;
  const user = JSON.parse(sessionStorage.getItem("BAT_USER"));
  const cropYear = year;
  const cropStageId = selectedCropStageId;

  // Update the fetchSurveyDetails function
  const fetchSurveyDetails = async () => {
    try {
      let headers = {
        apiCountryCode: selectedCountry,
      };

      const response = await SurveyManagementService.getViewSurvey(
        headerId,
        isVisitCompliance,
        cropStageId,
        cropYear,
        headers
      );

      if (response?.data?.response) {
        setViewSurveyData(response.data.response);
        const moduleNamesArray = response.data.response.map(
          (module) => module.moduleName
        );
        setModuleNames(moduleNamesArray);
        setActiveTab(moduleNamesArray[0] || "");
        setIsLoaded(true);
      }
    } catch (err) {
      console.error("Error fetching survey details:", err);

      if (err?.response?.status === 404) {
        // Handle 404 error (no record found)
        setSeverity("error");
        setMessage("No record found");
        setOpenAlert(true);
      } else {
        // Handle other errors
        setSeverity("error");
        setMessage("An error occurred while fetching data");
        setOpenAlert(true);
      }
    }
  };

  useEffect(() => {
    fetchSurveyDetails();
  }, [headerId, isVisitCompliance]);

  // Ensure fonts and images are fully loaded
  useEffect(() => {
    document.fonts.ready.then(() => setIsLoaded(true));
  }, []);

  const handleReopenClick = () => {
    if (status === "completed") {
      setReopenModalOpen(true);
    }
  };

  const handleReopenModalClose = () => {
    setReopenModalOpen(false);
    setReopenComments("");
  };

  const handleReopenCommentsChange = (e) => {
    setReopenComments(e.target.value);
  };
  const handleReopenConfirm = async () => {
    try {
      await SurveyManagementService.reopenSurvey(
        [headerId],
        reopenComments,
        {
          apiCountryCode: selectedCountry,
        },
        user?.userName
      );
      handleReopenModalClose();
      onRefreshData(); // Refresh the main table data
      onBack(); // Go back to main view
    } catch (error) {
      console.error("Error reopening survey:", error);
    }
  };

  const handleApproveClick = () => {
    if (status === "completed") {
      setApproveModalOpen(true);
    }
  };

  const handleApproveModalClose = () => {
    setApproveModalOpen(false);
  };

  const handleApproveConfirm = async () => {
    try {
      const headers = {
        apiCountryCode: selectedCountry,
      };
      await SurveyManagementService.approveSurvey(
        [headerId],
        headers,
        user?.userName
      );
      setApproveModalOpen(false);
      onRefreshData(); // Refresh the main table data
      onBack(); // Go back to main view
    } catch (error) {
      console.error("Error approving survey:", error);
    }
  };

  const handleDownloadPdfClick = () => {
    setDownloadPdfModalOpen(true);
  };

  const handleDownloadPdfModalClose = () => {
    setDownloadPdfModalOpen(false);
  };

  const handleSignatureClick = () => {
    if (status === "completed" || status === "approved") {
      onShowSignature();
    } else {
      setSignatureAlertOpen(true);
    }
  };

  const handleSignatureAlertClose = () => {
    setSignatureAlertOpen(false);
  };

  const toBase64 = (str) => {
    return btoa(
      new TextEncoder()
        .encode(str)
        .reduce((data, byte) => data + String.fromCharCode(byte), "")
    );
  };

  const handleDownloadPDF = async () => {
    setDownloadPdfModalOpen(false);
    setDownloadLoading(true);

    if (!signatureRef.current?.element) return;

    try {
      // Create PDF document
      const pdfDoc = await PDFDocument.create();
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      let page = pdfDoc.addPage([841.89, 595.28]);
      const { width, height } = page.getSize();

      const imageDataUrl = await toPng(signatureRef?.current?.element, {
        cacheBust: true,
        backgroundColor: "#ffffff",
        quality: 1,
        pixelRatio: 2,
      });

      const pngImageBytes = await fetch(imageDataUrl).then((res) =>
        res.arrayBuffer()
      );
      const pngImage = await pdfDoc.embedPng(pngImageBytes);

      const margin = 50; // same margin used in the rest of the doc
      const pageWidth = width; // width = 595.28 (portrait A4)
      const maxWidth = pageWidth - margin * 2;

      // Step 1: Get original image dimensions
      const pngDims = pngImage.scale(1); // full size
      const aspectRatio = pngDims.height / pngDims.width;

      // Step 2: Scale width to maxWidth and compute proportional height
      const finalWidth = maxWidth;
      const finalHeight = finalWidth * aspectRatio;
      // Draw image at top of page with margin
      page.drawImage(pngImage, {
        x: margin,
        y: height - margin - finalHeight, // Position from top
        width: finalWidth,
        height: finalHeight,
      });

      // Start content after the image
      let yOffset = height - margin - finalHeight - 50; // 50px spacing below the image
      // Adding 50px spacing above the image

      // Header styling
      const drawHeader = (text, y) => {
        // Main module header - teal color with partial width
        page.drawRectangle({
          x: 50,
          y: y - 30,
          width: 300, // Reduced width
          height: 40,
          color: rgb(0.13, 0.45, 0.6), // Teal color
        });
        page.drawText(text, {
          x: 60,
          y: y - 20,
          size: 16,
          font: helveticaBold,
          color: rgb(1, 1, 1),
        });
      };

      const drawSubmoduleHeader = (text, y) => {
        // Submodule header - dark blue with full width
        page.drawRectangle({
          x: 50,
          y: y - 25,
          width: width - 100,
          height: 35,
          color: rgb(0.06, 0.32, 0.54), // Dark blue
        });
        page.drawText(text, {
          x: 60,
          y: y - 12,
          size: 14,
          font: helveticaBold,
          color: rgb(1, 1, 1),
        });
      };

      const drawTableHeader = (y) => {
        // Question headers background - light gray
        page.drawRectangle({
          x: 50,
          y: y,
          width: width - 100,
          height: 30,
          color: rgb(0.95, 0.95, 0.95),
        });

        // Headers with white color for better visibility
        const headerStyle = {
          size: 12,
          font: helveticaBold,
          color: rgb(0, 0, 0), // Black color
        };

        // Question Code header
        page.drawText("Question Code", {
          x: 60,
          y: y + 10, // Adjusted Y position
          ...headerStyle,
        });

        // Question Text header
        page.drawText("Question Text", {
          x: 200,
          y: y + 10, // Adjusted Y position
          ...headerStyle,
        });

        // Answer header
        page.drawText("Answer", {
          x: 600,
          y: y + 10, // Adjusted Y position
          ...headerStyle,
        });

        // Blue line below headers
        page.drawRectangle({
          x: 50,
          y: y,
          width: width - 100,
          height: 2,
          color: rgb(0.06, 0.32, 0.54), // Dark blue
        });

        return y - 35; // Return new Y position
      };

      // Modified function to calculate text line wrapping and return the lines
      const calculateTextLines = (rawText, maxWidth, font, size) => {
        // Text sanitization
        const text = rawText
          ? rawText
            .replace(/[\r\n\t\f\v]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
          : "";

        const words = text.split(" ");
        const lines = [];
        let currentLine = "";

        for (const word of words) {
          // Skip empty words that might result from multiple spaces
          if (!word) continue;

          // Filter out any non-encodable characters
          const safeWord = word.replace(/[^\x20-\x7E]/g, "");

          const lineTest = currentLine
            ? `${currentLine} ${safeWord}`
            : safeWord;
          const lineWidth = font.widthOfTextAtSize(lineTest, size);

          if (lineWidth <= maxWidth) {
            currentLine = lineTest;
          } else {
            lines.push(currentLine);
            currentLine = safeWord;
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines;
      };

      const drawPreparedText = (
        lines1,
        lines2,
        x1,
        x2,
        y,
        size,
        font,
        color,
        lineHeight
      ) => {
        const linesLength = Math.max(lines1.length, lines2.length);
        let currentY = y;
        let i = 0;

        while (i < linesLength) {
          // Check if we need a new page
          if (currentY < 50) {
            // Create new page
            page = pdfDoc.addPage([841.89, 595.28]);
            // Start at a higher position on the new page to reduce gap
            currentY = height - 50;
          }

          // Draw text from first column
          if (i < lines1.length && lines1[i]) {
            try {
              page.drawText(lines1[i], {
                x: x1,
                y: currentY,
                size,
                font,
                color,
              });
            } catch (err) {
              console.warn(`Could not draw text line`, err);
              // Draw fallback text for this line
              page.drawText("[Text contains unsupported characters]", {
                x: x1,
                y: currentY,
                size,
                font,
                color,
              });
            }
          }

          // Draw text from second column
          if (i < lines2.length && lines2[i]) {
            try {
              page.drawText(lines2[i], {
                x: x2,
                y: currentY,
                size,
                font,
                color,
              });
            } catch (err) {
              console.warn(`Could not draw text line`, err);
              // Draw fallback text for this line
              page.drawText("[Text contains unsupported characters]", {
                x: x2,
                y: currentY,
                size,
                font,
                color,
              });
            }
          }

          // Move to next line
          currentY -= lineHeight;
          i++;
        }

        // Return the final y position after all lines are drawn
        return currentY;
      };

      const drawQuestionRow = async (detail, y) => {
        // Sanitize and prepare question and answer text
        const questionText = detail?.questionnaire?.questionTextEng || "";
        let newResponseText = detail?.responseStandard || detail?.responseText
        const responseText = newResponseText
          ? newResponseText
            .replace(/[\r\n\t\f\v]/g, " ")
            .replace(/\s+/g, " ")
            .replace(/[^\x20-\x7E]/g, "")
            .trim()
          : "";

        const imagesHeight =
          Math.ceil(detail?.picUrlList?.length / 7 || 0) * 23;

        // Calculate lines for both question and answer
        const questionLines = calculateTextLines(
          questionText,
          380, // Question text column width
          helveticaFont,
          10
        );

        const responseLines = calculateTextLines(
          responseText,
          200, // Response text column width
          helveticaFont,
          10
        );

        const lineHeight = 12; // line height for both columns
        const questionHeight = questionLines.length * lineHeight;
        const responseHeight = responseLines.length * lineHeight;

        // Total row height is the maximum of both columns
        const totalRowHeight = Math.max(
          questionHeight,
          responseHeight,
          imagesHeight
        );

        // Draw horizontal lines for the row
        const lineColor = rgb(0.8, 0.8, 0.8);

        // Top border
        page.drawLine({
          start: { x: 50, y: y + 60 },
          end: { x: width - 50, y: y + 60 },
          thickness: 1,
          color: lineColor,
        });

        // Bottom border
        page.drawLine({
          start: { x: 50, y: y + 40 - totalRowHeight },
          end: { x: width - 50, y: y + 40 - totalRowHeight },
          thickness: 1,
          color: lineColor,
        });

        // Question code (remains unchanged)
        page.drawText(detail?.questionnaire?.questionCode || "", {
          x: 60,
          y: y + 40,
          size: 10,
          font: helveticaFont,
          color: rgb(0.2, 0.2, 0.2),
          maxWidth: 110,
        });

        if (detail?.picUrlList) {
          for (let index = 0; index < detail?.picUrlList.length; index++) {
            const url = detail?.picUrlList[index];
            const res = await SurveyManagementService.getImageUrl(
              toBase64(url)
            );
            const imageUrl =
              "data:image/jpeg;base64," + res?.data?.response?.fileContent;

            if (imageUrl) {
              const imageBytes = await fetch(imageUrl).then((res) =>
                res.arrayBuffer()
              );
              let image;
              if (imageUrl.startsWith('data:image/jpeg') || imageUrl.startsWith('data:image/jpg')) {
                image = await pdfDoc.embedJpg(imageBytes);
              } else if (imageUrl.startsWith('data:image/png')) {
                image = await pdfDoc.embedPng(imageBytes);
              } else {
                // For other formats, try JPEG as fallback
                try {
                  image = await pdfDoc.embedJpg(imageBytes);
                } catch (error) {
                  console.error(`Failed to embed image at index ${index}:`, error);
                  continue; // Skip this image and continue with the next one
                }
              }

              const row = Math.floor(index / 7);
              const col = index % 7;

              page.drawImage(image, {
                x: 590 + col * 30,
                y: y + 30 - row * 30, // decrease Y to move "down"
                width: 20,
                height: 20,
              });
            }
          }
        }

        const currentY = drawPreparedText(
          questionLines,
          responseLines,
          190, // X position of question text
          590,
          y + 40, // Y position
          10, // font size
          helveticaFont,
          rgb(0.2, 0.2, 0.2),
          lineHeight
        );

        // Bottom border
        currentY > totalRowHeight &&
          totalRowHeight !== imagesHeight &&
          page.drawLine({
            start: { x: 50, y: currentY },
            end: { x: width - 50, y: currentY },
            thickness: 1,
            color: lineColor,
          });

        // Return the new Y position
        return totalRowHeight > imagesHeight
          ? currentY - 70
          : y - 30 - totalRowHeight;
      };

      for (const module of viewSurveyData) {
        // Use viewSurveyData instead of surveyData
        if (yOffset < 300) {
          page = pdfDoc.addPage([841.89, 595.28]);
          yOffset = height - 50;
        }
        drawHeader(module.moduleName, yOffset);
        yOffset -= 60;

        for (const subModule of module?.subModules) {
          if (yOffset < 200) {
            page = pdfDoc.addPage([841.89, 595.28]);
            yOffset = height - 50;
          }
          drawSubmoduleHeader(subModule?.subModuleName, yOffset);
          yOffset -= 50;

          yOffset = drawTableHeader(yOffset);
          yOffset -= 40;

          for (const detail of subModule?.surveyDetails) {
            // Check if we need a new page
            if (yOffset < 50) {
              page = pdfDoc.addPage([841.89, 595.28]);
              yOffset = height - 100;
            }

            yOffset = await drawQuestionRow(detail, yOffset);
          }

          yOffset -= 30; // Add extra spacing between submodule sections
        }
      }

      // Get current date and time
      const now = new Date();
      const dateString = now.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });

      // Draw "Downloaded at {date time}" footer
      page.drawText(`${dateString}`, {
        x: 50,
        y: 30, // Near bottom margin
        size: 10,
        font: helveticaFont,
        color: rgb(0.5, 0.5, 0.5),
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${cropStageTitle}_${surveyData?.allFarmerDetails?.length
          ? surveyData?.allFarmerDetails.map((f) => f.farmerName).join(", ")
          : "Farmer"
        }_${status.charAt(0).toUpperCase() + status.slice(1)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation error:", err);
    } finally {
      setDownloadPdfModalOpen(false);
      setDownloadLoading(false);
    }
  };

  // Update the renderTabContent function to use formattedData
  const renderTabContent = () => {
    // if (!viewSurveyData) return null;

    // Get the data for the active tab
    const currentModule = viewSurveyData.find(
      (module) => module.moduleName === activeTab
    );

    if (!currentModule) return null;

    return (
      <Box>
        {currentModule.subModules.map((submodule) => (
          <Box key={submodule.subModuleName} sx={{ marginBottom: 2 }}>
            <Box
              sx={{
                backgroundColor: "#004F9F",
                color: "white",
                padding: "10px",
                fontWeight: "bold",
              }}
            >
              <Typography variant="subtitle1">
                {submodule.subModuleName}
              </Typography>
            </Box>

            {/* Table for questions */}
            <div className="table-container">
              <table className="view-survey-table">
                <thead>
                  <tr>
                    <th className="actions-header">
                      <div> {t("viewSurvey.questionCode")}</div>
                    </th>
                    <th className="actions-header">
                      <div>{t("viewSurvey.questionText")}</div>
                    </th>
                    <th className="actions-header">
                      <div> {t("viewSurvey.answer")}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Map through the submodule questions */}
                  {submodule.surveyDetails.map((item, index) => (
                    <tr key={index}>
                      <td>{item?.questionnaire?.questionCode}</td>
                      <td>{item?.questionnaire?.questionTextEng}</td>
                      <td>
                        {(item?.responseStandard || item.responseText)
                          ? item?.responseStandard || item.responseText
                          : item.picUrlList?.length > 0
                            ? item.picUrlList.map((url, i) => (
                              <ViewImage key={i} fileUrl={url} />
                            ))
                            : " "}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Box>
        ))}
      </Box>
    );
  };

  const renderReopenModal = () => (
    <Modal open={reopenModalOpen} onClose={handleReopenModalClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: "4px",
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0e3364",
            color: "white",
            padding: "15px 20px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">{t("viewSurvey.confirmation")}</Typography>
        </Box>
        {/* Content */}
        <Box sx={{ padding: "20px" }}>
          <Typography sx={{ mb: 2 }}>
            {t("viewSurvey.reopenConfirmationMsg")}
          </Typography>

          {/* TextField with label on the border */}
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={reopenComments}
            onChange={handleReopenCommentsChange}
            placeholder=""
            label={t("viewSurvey.comments")}
            InputLabelProps={{
              shrink: true,
              sx: {
                backgroundColor: "white",
                padding: "0 5px",
                fontSize: "0.875rem",
                fontWeight: "normal",
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#D0D0D0",
                },
                "&:hover fieldset": {
                  borderColor: "#999999",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#004F9F",
                },
              },
              mb: 2,
            }}
          />

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#6c757d",
                color: "white",
                "&:hover": { bgcolor: "#5a6268" },
                width: 100,
              }}
              onClick={handleReopenModalClose}
            >
              {t("viewSurvey.no")}
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: reopenComments.trim() ? "#004F9F" : "#87a9e0",
                color: "white",
                "&:hover": {
                  bgcolor: reopenComments.trim() ? "#004F9F" : "#87a9e0",
                },
                width: 100,
              }}
              onClick={handleReopenConfirm}
              disabled={!reopenComments.trim()}
            >
              {t("viewSurvey.yes")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  const renderApproveModal = () => (
    <Modal open={approveModalOpen} onClose={handleApproveModalClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: "4px",
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0e3364",
            color: "white",
            padding: "15px 20px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">{t("viewSurvey.confirmation")}</Typography>
        </Box>

        {/* Content */}
        <Box sx={{ padding: "20px" }}>
          <Typography sx={{ mb: 3, textAlign: "center" }}>
            {t("viewSurvey.approveConfirmationMsg")}
          </Typography>

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#6c757d",
                color: "white",
                "&:hover": { bgcolor: "#5a6268" },
                width: 100,
              }}
              onClick={handleApproveModalClose}
            >
              {t("viewSurvey.no")}
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#0066b2",
                color: "white",
                "&:hover": { bgcolor: "#00559b" },
                width: 100,
              }}
              onClick={handleApproveConfirm}
            >
              {t("viewSurvey.yes")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  const renderDownloadPdfModal = () => (
    <Modal open={downloadPdfModalOpen} onClose={handleDownloadPdfModalClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: "4px",
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0e3364",
            color: "white",
            padding: "15px 20px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6"> {t("viewSurvey.confirmation")}</Typography>
        </Box>

        {/* Content */}
        <Box sx={{ padding: "20px" }}>
          <Typography sx={{ mb: 3, textAlign: "center" }}>
            {t("viewSurvey.pdfDownloadConfirmationMsg")}
          </Typography>

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#6c757d",
                color: "white",
                "&:hover": { bgcolor: "#5a6268" },
                width: 100,
              }}
              onClick={handleDownloadPdfModalClose}
            >
              {t("viewSurvey.no")}
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#0066b2",
                color: "white",
                "&:hover": { bgcolor: "#00559b" },
                width: 100,
              }}
              onClick={handleDownloadPDF}
            >
              {t("viewSurvey.yes")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  const renderSignatureAlertModal = () => (
    <Modal open={signatureAlertOpen} onClose={handleSignatureAlertClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: "4px",
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0e3364",
            color: "white",
            padding: "15px 20px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">{t("viewSurvey.info")}</Typography>
        </Box>

        {/* Content */}
        <Box sx={{ padding: "20px" }}>
          <Typography sx={{ mb: 3, textAlign: "center" }}>
            {t("viewSurvey.signatureInfoMsg")}
          </Typography>

          {/* OK Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#0066b2",
                color: "white",
                "&:hover": { bgcolor: "#00559b" },
                width: 100,
              }}
              onClick={handleSignatureAlertClose}
            >
              {t("viewSurvey.ok")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  return (
    <>
      <CustomDivider
        title={cropStageTitle}
        onBack={onBack}
        CustomButton={
          <Box>
            <Button
              sx={{
                bgcolor: "red",
                color: "white",
                fontSize: "0.5rem",
                marginRight: "15px",
                padding: "6px 13px",
                borderRadius: "3px",
                "&:hover": { bgcolor: "#d32f2f" },
                "&.Mui-disabled": {
                  backgroundColor: "#FFB4B4",
                  color: "#FFFFFF",
                  cursor: "not-allowed",
                },
              }}
              onClick={handleReopenClick}
              disabled={
                surveyData?.status?.toLowerCase() !== "completed" ||
                isVisitCompliance === true ||
                isGlobalProfile ||
                !isApprover
              }
            >
              {t("viewSurvey.reopen")}
            </Button>
            <Button
              sx={{
                bgcolor: "green",
                color: "white",
                fontSize: "0.5rem",
                padding: "6px 13px",
                borderRadius: "3px",
                "&:hover": { bgcolor: "#2e7d32" },
                "&.Mui-disabled": {
                  backgroundColor: "#A8D5A5",
                  color: "#FFFFFF",
                  cursor: "not-allowed",
                },
              }}
              disabled={
                surveyData?.status?.toLowerCase() !== "completed" ||
                isVisitCompliance === true ||
                isGlobalProfile ||
                !isApprover
              }
              onClick={handleApproveClick}
            >
              {t("viewSurvey.approve")}
            </Button>
          </Box>
        }
      />

      {/* Use moduleNames state for tabs */}
      <Box sx={{ display: "flex", borderBottom: "1px solid #e0e0e0" }}>
        {moduleNames?.map((name) => (
          <Box
            key={name}
            sx={{
              position: "relative",
              padding: "10px 20px",
              cursor: "pointer",
            }}
            onClick={() => setActiveTab(name)}
          >
            <Typography
              sx={{
                color: activeTab === name ? "#ff8c00" : "inherit",
                fontWeight: activeTab === name ? "bold" : "normal",
              }}
            >
              {name}
            </Typography>
            {activeTab === name && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: "3px",
                  backgroundColor: "#ff8c00",
                }}
              />
            )}
          </Box>
        ))}
        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          <Button
            sx={{
              textTransform: "none",
              color: "#0066cc",
              fontSize: "14px",
              padding: "10px 15px",
              textDecoration: "underline !important",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#0066cc",
                boxShadow: "none",
              },
            }}
            onClick={() => {
              handleSignatureClick();
            }}
          >
            {t("viewSurvey.signaturePage")}
          </Button>

          <Button
            sx={{
              textTransform: "none",
              color: "#0066cc",
              fontSize: "14px",
              padding: "10px 15px",
              textDecoration: "underline !important",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#0066cc",
                boxShadow: "none",
              },
            }}
            onClick={handleDownloadPdfClick}
            disabled={
              !isLoaded ||
              !["completed", "approved"].includes(status) ||
              downloadLoading ||
              !signaturePageLoaded
            }
          >
            {downloadLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <span>Downloading...</span>
              </Box>
            ) : (
              t("viewSurvey.downloadPDF")
            )}
          </Button>
        </Box>
      </Box>

      {/* Use formattedData for content */}
      <Box sx={{ mb: 10 }}>{renderTabContent()}</Box>

      {/* Hidden Signature Page */}
      {["completed", "approved"].includes(status) && (
        <Box
          sx={{
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
          }}
        >
          <SignaturePage
            ref={signatureRef}
            data={surveyData} // Pass original survey data for signatures
            onSignatureLoad={setSignaturePageLoaded}
            onBack={onBack}
            isVisitCompliance={isVisitCompliance}
          />
        </Box>
      )}

      {/* Render the modals */}
      {renderReopenModal()}
      {renderApproveModal()}
      {renderDownloadPdfModal()}
      {renderSignatureAlertModal()}
    </>
  );
};

export default ViewSurvey;
